import { useMutation, useQuery, useQueryClient } from 'react-query'
import { b64_to_utf8 } from 'utils'

import { API_ENDPOINTS } from 'config/api'
import axios from 'config/axios'

import {
  AllBackendChallengesDetails,
  AllBackendChallengesResponse,
  BacendResultDetails,
  BackendChallengeDetails,
  BackendChallengeResponse,
  BackendResultsResponse,
  BackendSubmissionHistory,
  BackendSubmissionsResponse
} from './backend.types'

export async function fetchAllBackendChallenges() {
  const response = axios.get(API_ENDPOINTS.BACKEND_CHALLENGES)
  const { data } = (await (await response).data) as AllBackendChallengesResponse

  return data.map((challenge) => ({
    id: challenge.id,
    ...challenge.attributes
  }))
}

export function useAllBackendChallenges({
  enabled = true
}: {
  enabled?: boolean
}) {
  return useQuery<AllBackendChallengesDetails[], null>(
    ['backend_challenges'],
    fetchAllBackendChallenges,
    {
      enabled
    }
  )
}

/// utlis
const removeSlashFromFiles = (files: Record<string, string>) => {
  let utfFiles = {}
  Object.keys(files).forEach((key: string) => {
    const newKey = key.substring(1)
    utfFiles = { ...utfFiles, [newKey]: b64_to_utf8(files[key]) }
  })

  return utfFiles
}

const removeSlash = (paths: string[]) => {
  return paths.map((path) => path.substring(1))
}

export async function fetchChallenge(id: string) {
  const response = axios.get(`${API_ENDPOINTS.BACKEND_CHALLENGES}/${id}`)
  const { data } = (await (await response).data) as BackendChallengeResponse

  const {
    files,
    active_path,
    open_paths,
    hidden_files,
    previous_data,
    ...rest
  } = data.attributes

  return {
    id: data.id,
    active_path: active_path ? active_path.substring(1) : null,
    open_paths: removeSlash(open_paths),
    hidden_files: removeSlash(hidden_files),
    previous_data: removeSlashFromFiles(previous_data),
    files: removeSlashFromFiles(files),
    ...rest
  }
}

export function useBackendChallenge(id: string) {
  return useQuery<BackendChallengeDetails, null>(
    ['backend_challenge', { id }],
    () => fetchChallenge(id),
    {
      enabled: !!id
    }
  )
}

export type SubmissionParams = {
  user_id: number
  backend_challenge_id: number
  submitted_url: string
}

export async function fetchCreateSubmission(
  submission_params: SubmissionParams
) {
  const { user_id, backend_challenge_id, submitted_url } = submission_params

  const response = await axios.post(
    API_ENDPOINTS.BACKEND_CHALLENGES_SUBMISSION,
    {
      data: {
        type: 'be_submissions',
        attributes: {
          user_id,
          backend_challenge_id,
          submitted_url
        }
      }
    }
  )

  const { data } = (await response.data) as BackendSubmissionsResponse
  return {
    id: data.id,
    ...data.attributes
  }
}

export const useCreateSubmissionMutation = () => {
  const queryClient = useQueryClient()

  return useMutation(
    (submission_params: SubmissionParams) =>
      fetchCreateSubmission(submission_params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['submissions'])
      }
    }
  )
}

async function fetchSubmission(id: string) {
  const response = await axios.get(
    `${API_ENDPOINTS.BACKEND_CHALLENGES_SUBMISSION}/${id}`
  )
  const { data } = (await response.data) as BackendResultsResponse
  return {
    id: data.id,
    ...data.attributes
  }
}

export function useSubmissionResult(id: string) {
  return useQuery<BacendResultDetails, null>(
    ['backend_submission_result'],
    () => fetchSubmission(id),
    {
      enabled: !!id,
      refetchOnWindowFocus: 'always'
    }
  )
}

const fetchBackendChallengeHistory = async ({
  user_id,
  backend_challenge_id
}: {
  user_id: number
  backend_challenge_id: string
}) => {
  const response = await axios.get(
    `${API_ENDPOINTS.BACKEND_CHALLENGES_SUBMISSION}?filter[user_id]=${user_id}&filter[backend_challenge_id]=${backend_challenge_id}`
  )
  const { data } = (await response.data) as BackendSubmissionHistory

  return data.reverse().map((submission) => ({
    id: submission.id,
    ...submission.attributes
  }))
}

export const useBackendChallengeHistory = ({
  user_id,
  backend_challenge_id
}: {
  user_id: number
  backend_challenge_id: string
}) => {
  return useQuery<BacendResultDetails[], null>(
    ['backend-submission-history', { backend_challenge_id }],
    () => fetchBackendChallengeHistory({ user_id, backend_challenge_id })
  )
}
